import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { ABOUT } from '../data/about';
import { ASSETS, CENTRES, MEDIA_GALLERY } from '../data/content';
import {
  Award, MapPin, Sparkles, Heart, Stethoscope, BookOpen, Users, Calendar, ArrowRight,
  CheckCircle2, Mic, Video, Building2,
} from 'lucide-react';

const ICONS = [Mic, Video, Building2, BookOpen];

export default function About() {
  const { lang } = useLanguage();

  return (
    <div data-testid="about-page" className="animate-fade-in-up">
      {/* Hero */}
      <section className="container-app pt-12 sm:pt-20 pb-10">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sage-light text-sage text-xs font-semibold uppercase tracking-wider mb-5">
          <Heart size={12} /> {lang === 'zh' ? '我们的故事' : 'Our story'}
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-ink leading-tight tracking-tight mb-6">
          {lang === 'zh' ? '关于 You & Me Lactation Coach' : 'About You & Me Lactation Coach'}
        </h1>
        <div className="relative rounded-3xl overflow-hidden bg-subtle shadow-sm" data-testid="about-hero-image-wrap">
          <img
            src={ASSETS.aboutHero}
            alt={lang === 'zh' ? '我们的团队' : 'Our team'}
            loading="lazy"
            /* Less aggressive crop than 21/9 so heads stay fully visible.
               object-position pinned slightly above centre keeps faces in frame on every aspect ratio. */
            className="w-full aspect-[4/3] sm:aspect-[16/9] object-cover object-[center_25%]"
            data-testid="about-hero-image"
          />
        </div>
      </section>

      {/* Founder */}
      <section className="container-app py-8">
        <div className="card-base p-6 sm:p-10 grid md:grid-cols-[260px_1fr] gap-6 md:gap-10 items-start">
          <div className="relative">
            <div className="absolute -top-3 -left-3 w-16 h-16 rounded-full bg-terracotta-light/70 blur-2xl" aria-hidden="true" />
            <img
              src={ASSETS.founder}
              alt={lang === 'zh' ? '创办人 Soo Li Theng' : 'Founder Soo Li Theng'}
              loading="lazy"
              className="relative w-full md:w-60 aspect-square rounded-3xl object-cover border-4 border-paper shadow-md"
              data-testid="about-founder-image"
            />
            <p className="mt-3 text-center text-[11px] uppercase tracking-[0.18em] text-ink-tertiary">
              {lang === 'zh' ? '创办人 / Director Soo Li Theng' : 'Founder / Director Soo Li Theng'}
            </p>
          </div>
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta mb-2 block">
              {lang === 'zh' ? '创办人' : 'Founder'}
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-4">
              {lang === 'zh' ? ABOUT.founder.heading_zh : ABOUT.founder.heading_en}
            </h2>
            <p className="text-ink-secondary leading-relaxed">
              {lang === 'zh' ? ABOUT.founder.body_zh : ABOUT.founder.body_en}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {[
                lang === 'zh' ? '注册护士' : 'Registered Nurse',
                lang === 'zh' ? '认证讲师' : 'Certified Trainer',
                lang === 'zh' ? '泌乳辅导员' : 'Lactation Peer Counsellor',
                lang === 'zh' ? '15 年护理经验' : '15 yrs nursing',
              ].map((tag) => (
                <span key={tag} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-sage-light/70 text-sage">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team support note */}
      <section className="container-app py-8" data-testid="about-team">
        <div className="card-base p-5 sm:p-7 flex gap-4 items-start bg-sage-light/30">
          <Users size={20} className="text-sage flex-shrink-0 mt-1" />
          <p className="text-sm text-ink-secondary leading-relaxed">
            {lang === 'zh'
              ? '我们的团队会根据妈妈的情况，提供亲喂、挤奶、涨奶、堵奶、小白点、奶量和泌乳陪跑支持。'
              : 'Our team supports mothers with latch, pumping, engorgement, clogged ducts, milk blebs, milk supply, and breastfeeding coaching based on each mother\u2019s situation.'}
          </p>
        </div>
      </section>

      {/* Background */}
      <section className="container-app py-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-2xl bg-terracotta-light flex items-center justify-center">
            <Stethoscope size={18} className="text-terracotta" />
          </div>
          <h2 className="font-heading text-2xl font-semibold text-ink">
            {lang === 'zh' ? ABOUT.background.heading_zh : ABOUT.background.heading_en}
          </h2>
        </div>
        <ul className="grid sm:grid-cols-2 gap-3">
          {(lang === 'zh' ? ABOUT.background.bullets_zh : ABOUT.background.bullets_en).map((b, i) => (
            <li
              key={i}
              data-testid={`background-bullet-${i}`}
              className="card-base p-4 flex gap-3 items-start text-sm leading-relaxed text-ink-secondary"
            >
              <CheckCircle2 size={16} className="text-sage flex-shrink-0 mt-0.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Centres */}
      <section className="container-app py-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-2xl bg-sage-light flex items-center justify-center">
            <MapPin size={18} className="text-sage" />
          </div>
          <h2 className="font-heading text-2xl font-semibold text-ink">
            {lang === 'zh' ? ABOUT.centres.heading_zh : ABOUT.centres.heading_en}
          </h2>
        </div>
        <p className="text-ink-secondary mb-6 max-w-2xl">
          {lang === 'zh' ? ABOUT.centres.body_zh : ABOUT.centres.body_en}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="about-centre-grid">
          {CENTRES.map((c) => (
            <div
              key={c.id}
              data-testid={`centre-${c.id}`}
              className="card-base aspect-[3/2] p-3 flex flex-col items-center justify-center text-center bg-paper"
            >
              {c.image ? (
                <img
                  src={c.image}
                  alt={lang === 'zh' ? c.name_zh : c.name_en}
                  loading="lazy"
                  className="max-h-12 sm:max-h-14 w-auto object-contain mb-1.5"
                />
              ) : (
                <div className="w-10 h-10 rounded-xl bg-subtle flex items-center justify-center mb-2">
                  <Building2 size={16} className="text-ink-tertiary" />
                </div>
              )}
              <span className="text-[11px] sm:text-xs font-medium text-ink leading-tight">
                {lang === 'zh' ? c.name_zh : c.name_en}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Media */}
      <section className="container-app py-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-2xl bg-terracotta-light flex items-center justify-center">
            <Mic size={18} className="text-terracotta" />
          </div>
          <h2 className="font-heading text-2xl font-semibold text-ink">
            {lang === 'zh' ? ABOUT.media.heading_zh : ABOUT.media.heading_en}
          </h2>
        </div>
        <p className="text-ink-secondary mb-6 max-w-2xl">
          {lang === 'zh' ? ABOUT.media.body_zh : ABOUT.media.body_en}
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="about-media-grid">
          {MEDIA_GALLERY.map((g, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <div
                key={g.id}
                data-testid={`gallery-${g.id}`}
                className="card-base relative aspect-[4/3] overflow-hidden flex flex-col items-center justify-center p-4 text-center bg-gradient-to-br from-sage-light/60 via-paper to-terracotta-light/40"
              >
                {g.image ? (
                  <>
                    <img
                      src={g.image}
                      alt={lang === 'zh' ? g.label_zh : g.label_en}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-x-0 bottom-0 px-3 py-2 bg-gradient-to-t from-black/70 via-black/35 to-transparent">
                      <span className="text-[11px] sm:text-xs font-medium text-white drop-shadow-sm">
                        {lang === 'zh' ? g.label_zh : g.label_en}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-12 h-12 rounded-2xl bg-paper/80 backdrop-blur flex items-center justify-center mb-2">
                      <Icon size={18} className="text-ink-secondary" />
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-ink-secondary">
                      {lang === 'zh' ? g.label_zh : g.label_en}
                    </span>
                    <span className="text-[10px] text-ink-tertiary mt-1">
                      {lang === 'zh' ? '占位图' : 'Placeholder'}
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Technique */}
      <section className="container-app py-8">
        <div className="card-base p-6 sm:p-10 bg-gradient-to-br from-terracotta-light/30 to-paper">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-paper flex items-center justify-center">
              <Sparkles size={18} className="text-terracotta" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-ink">
              {lang === 'zh' ? ABOUT.technique.heading_zh : ABOUT.technique.heading_en}
            </h2>
          </div>
          <p className="text-ink-secondary leading-relaxed">
            {lang === 'zh' ? ABOUT.technique.body_zh : ABOUT.technique.body_en}
          </p>
          <p className="text-ink-secondary leading-relaxed mt-4 italic" data-testid="about-technique-warm">
            {lang === 'zh'
              ? '我们重视评估、轻柔处理和后续指导，而不是一开始就用强力硬推的方式处理乳房。'
              : 'We focus on assessment, gentle care, and follow-up guidance instead of forceful breast massage from the start.'}
          </p>
        </div>
      </section>

      {/* Comparison */}
      <section className="container-app py-8">
        <h2 className="font-heading text-2xl font-semibold text-ink mb-6">
          {lang === 'zh' ? ABOUT.comparison.heading_zh : ABOUT.comparison.heading_en}
        </h2>
        <div className="card-base overflow-hidden">
          <div className="grid grid-cols-[1fr_1fr_1fr] bg-subtle text-xs sm:text-sm font-semibold uppercase tracking-wider text-ink-secondary">
            <div className="p-4">·</div>
            <div className="p-4 border-l border-border/60">
              {lang === 'zh' ? ABOUT.comparison.col1_zh : ABOUT.comparison.col1_en}
            </div>
            <div className="p-4 border-l border-border/60 text-terracotta">
              {lang === 'zh' ? ABOUT.comparison.col2_zh : ABOUT.comparison.col2_en}
            </div>
          </div>
          {ABOUT.comparison.rows.map((r, i) => (
            <div
              key={i}
              data-testid={`comparison-row-${i}`}
              className={`grid grid-cols-[1fr_1fr_1fr] text-sm ${i % 2 === 0 ? 'bg-paper' : 'bg-subtle/30'}`}
            >
              <div className="p-4 font-medium text-ink">
                {lang === 'zh' ? r.label_zh : r.label_en}
              </div>
              <div className="p-4 border-l border-border/60 text-ink-secondary leading-relaxed">
                {lang === 'zh' ? r.a_zh : r.a_en}
              </div>
              <div className="p-4 border-l border-border/60 text-ink leading-relaxed">
                {lang === 'zh' ? r.b_zh : r.b_en}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why */}
      <section className="container-app py-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-2xl bg-sage-light flex items-center justify-center">
            <Award size={18} className="text-sage" />
          </div>
          <h2 className="font-heading text-2xl font-semibold text-ink">
            {lang === 'zh' ? ABOUT.why.heading_zh : ABOUT.why.heading_en}
          </h2>
        </div>
        <ul className="grid sm:grid-cols-2 gap-3">
          {(lang === 'zh' ? ABOUT.why.bullets_zh : ABOUT.why.bullets_en).map((b, i) => (
            <li
              key={i}
              data-testid={`why-bullet-${i}`}
              className="card-base p-4 flex gap-3 items-start text-sm leading-relaxed text-ink-secondary"
            >
              <CheckCircle2 size={16} className="text-terracotta flex-shrink-0 mt-0.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* CTA */}
      <section className="container-app py-12">
        <div className="card-base p-6 sm:p-10 bg-gradient-to-br from-terracotta-light/40 to-sage-light/30 text-center">
          <h3 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-6 max-w-2xl mx-auto">
            {lang === 'zh' ? ABOUT.cta.heading_zh : ABOUT.cta.heading_en}
          </h3>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/self-check" className="btn-primary" data-testid="about-cta-selfcheck">
              {lang === 'zh' ? '开始 Self-check' : 'Start Self-check'} <ArrowRight size={16} className="ml-2" />
            </Link>
            <Link to="/booking" className="btn-secondary" data-testid="about-cta-book">
              <Calendar size={16} className="mr-2" /> {lang === 'zh' ? '预约咨询' : 'Book Consultation'}
            </Link>
            <Link to="/consultants" className="btn-outline" data-testid="about-cta-consultants">
              <Users size={16} className="mr-2" /> {lang === 'zh' ? '查看顾问团队' : 'View Consultants'}
            </Link>
            <Link to="/library" className="btn-outline" data-testid="about-cta-library">
              <BookOpen size={16} className="mr-2" /> {lang === 'zh' ? '浏览资料库' : 'Browse Library'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
